// caldav-provider.js — RFC 4791 (CalDAV) implementation.
//
// Works against any standards-compliant CalDAV server: Fastmail, Nextcloud,
// Radicale, Baikal, mailcow, Posteo. The iCloud subclass adds Apple-
// specific quirks. The core flow is:
//
//   1. PROPFIND `current-user-principal`  → user principal URL
//   2. PROPFIND `calendar-home-set`       → calendar collection root
//   3. PROPFIND on the home set            → list of calendars (DAV:resourcetype
//                                            includes CALDAV:calendar)
//   4. REPORT `calendar-multiget` or       → events in a date range, with
//      `calendar-query`                     ETag + iCalendar body
//   5. PUT + If-Match                      → create / update
//   6. DELETE + If-Match                   → delete
//
// Authentication is HTTP Basic with an app-specific password (Apple, Google
// over CalDAV) or the user's account password (most self-hosted setups).
// Tokens never reach the server; basic auth lives in the request and is
// proxied by the Supabase relay on web.

import { ProviderRegistry } from '../provider-registry.js';
import { CalendarProvider, CalendarProviderError, ERROR_CODES } from './calendar-provider.js';
import { ICalCodec as Codec } from './ical-codec.js';
import { CalendarTransport as Transport } from './transport.js';

export class CalDavProvider extends CalendarProvider {
  /** @param {any} [opts] */
  constructor(opts: any) {
    super({
      ...opts,
      id: opts?.id || 'caldav',
      capabilities: { etag: true, rrule: true, delta: false, ...(opts?.capabilities || {}) },
    });
    // Discovered on init().
    this._principalUrl = null;
    this._homeUrl = null;
    this._creds = null; // { user, pass }
  }

  // ── Credentials ──
  /** @returns {Promise<any>} the { user, pass } basic-auth creds. */
  async _loadCreds() {
    if (this._creds) return this._creds;
    if (!this.credentialKey) {
      throw new CalendarProviderError(ERROR_CODES.AUTH_REQUIRED, 'no credentialKey');
    }
    // `|| null` drops the `false` arm so vault is WorkspaceSessionVault | null and
    // the ?. below narrows cleanly (same falsy runtime behaviour).
    const vault = (typeof window !== 'undefined' && window.WorkspaceSessionVault) || null;
    if (!vault?.load) {
      throw new CalendarProviderError(ERROR_CODES.AUTH_REQUIRED, 'vault unavailable');
    }
    const raw = await vault.load(this.credentialKey);
    if (!raw) throw new CalendarProviderError(ERROR_CODES.AUTH_REQUIRED, 'no credentials');
    this._creds = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return this._creds;
  }

  /** @returns {any} the shared transport request options. */
  _requestOpts() {
    return {
      accountId: this.accountId,
      credentials: { kind: 'basic', user: this._creds?.user, pass: this._creds?.pass },
      useCaldav: true,
    };
  }

  // ── Init / discovery ──
  /** @returns {Promise<void>} */
  async init() {
    await super.init();
    await this._loadCreds();
    if (!this.serverUrl) {
      throw new CalendarProviderError(ERROR_CODES.UNSUPPORTED, 'no serverUrl');
    }
    // Skip discovery if already cached on the spec.
    if (!this._principalUrl) await this._discoverPrincipal();
    if (!this._homeUrl) await this._discoverHomeSet();
  }

  /**
   * @param {string} url
   * @param {string} body
   * @param {string} [depth]
   * @returns {Promise<string>}
   */
  async _propfind(url: any, body: any, depth = '0') {
    const resp = await Transport.request({
      url,
      method: 'PROPFIND',
      headers: {
        'Content-Type': 'application/xml; charset=utf-8',
        Depth: depth,
      },
      body,
      ...this._requestOpts(),
    });
    return await resp.text();
  }

  /** @returns {Promise<void>} */
  async _discoverPrincipal() {
    const body = `<?xml version="1.0" encoding="utf-8" ?>
<d:propfind xmlns:d="DAV:">
<d:prop><d:current-user-principal/></d:prop>
</d:propfind>`;
    const xml = await this._propfind(this.serverUrl, body, '0');
    const cup = _firstEl(_parseXml(xml), 'current-user-principal');
    const href = cup && _firstText(cup, 'href');
    if (!href)
      throw new CalendarProviderError(ERROR_CODES.AUTH_REQUIRED, 'principal not discovered');
    this._principalUrl = _absoluteUrl(this.serverUrl, href.trim());
  }

  /** @returns {Promise<void>} */
  async _discoverHomeSet() {
    const body = `<?xml version="1.0" encoding="utf-8" ?>
<d:propfind xmlns:d="DAV:" xmlns:c="urn:ietf:params:xml:ns:caldav">
<d:prop><c:calendar-home-set/></d:prop>
</d:propfind>`;
    const xml = await this._propfind(this._principalUrl as string, body, '0');
    const home = _firstEl(_parseXml(xml), 'calendar-home-set');
    const href = home && _firstText(home, 'href');
    if (!href)
      throw new CalendarProviderError(ERROR_CODES.UNSUPPORTED, 'calendar-home-set not advertised');
    this._homeUrl = _absoluteUrl(this.serverUrl, href.trim());
  }

  // ── listCalendars ──
  /** @returns {Promise<any[]>} */
  async listCalendars() {
    const body = `<?xml version="1.0" encoding="utf-8" ?>
<d:propfind xmlns:d="DAV:" xmlns:c="urn:ietf:params:xml:ns:caldav" xmlns:cs="http://calendarserver.org/ns/" xmlns:ic="http://apple.com/ns/ical/">
<d:prop>
  <d:displayname/>
  <d:resourcetype/>
  <ic:calendar-color/>
  <cs:getctag/>
  <c:supported-calendar-component-set/>
</d:prop>
</d:propfind>`;
    const xml = await this._propfind(this._homeUrl as string, body, '1');
    const list = [];
    for (const response of _responseEls(_parseXml(xml))) {
      // Only collections whose resourcetype includes a CALDAV:calendar — skips
      // the home collection itself, address books, and other DAV collections.
      if (!_isCalendarCollection(response)) continue;
      // Skip VTODO-only collections — we surface events only.
      const comps = _supportedComps(response);
      if (comps.length && comps.includes('VTODO') && !comps.includes('VEVENT')) continue;
      const href = _responseHref(response);
      if (!href) continue;
      const name = _firstText(response, 'displayname');
      const color = _firstText(response, 'calendar-color');
      list.push({
        id: _absoluteUrl(this._homeUrl as string, href.trim()),
        title: (name || '').trim() || href.split('/').filter(Boolean).pop(),
        color: color ? color.trim().slice(0, 7) : null,
        writable: true,
      });
    }
    return list;
  }

  // ── fetchEvents (calendar-query) ──
  /**
   * @param {any} opts - { calendarId, startDate, endDate }
   * @returns {Promise<{ events: any[] }>}
   */
  async fetchEvents({ calendarId, startDate, endDate }: any) {
    if (!calendarId) throw new TypeError('fetchEvents: calendarId required');
    const start = _toCaldavDt(startDate);
    const end = _toCaldavDt(endDate);
    const body = `<?xml version="1.0" encoding="utf-8" ?>
<c:calendar-query xmlns:d="DAV:" xmlns:c="urn:ietf:params:xml:ns:caldav">
<d:prop>
  <d:getetag/>
  <c:calendar-data/>
</d:prop>
<c:filter>
  <c:comp-filter name="VCALENDAR">
    <c:comp-filter name="VEVENT">
      <c:time-range start="${start}" end="${end}"/>
    </c:comp-filter>
  </c:comp-filter>
</c:filter>
</c:calendar-query>`;
    const resp = await Transport.request({
      url: calendarId,
      method: 'REPORT',
      headers: {
        'Content-Type': 'application/xml; charset=utf-8',
        Depth: '1',
      },
      body,
      ...this._requestOpts(),
    });
    const xml = await resp.text();
    return { events: this._parseMultistatus(xml, calendarId) };
  }

  /**
   * @param {string} xml
   * @param {string} calendarId
   * @returns {any[]}
   */
  _parseMultistatus(xml: any, calendarId: any) {
    const events = [];
    for (const response of _responseEls(_parseXml(xml))) {
      const href = _responseHref(response);
      // textContent already decodes XML entities, so the iCalendar body is raw.
      const data = _firstText(response, 'calendar-data');
      if (!href || data == null) continue;
      const etag = _firstText(response, 'getetag');
      const parsed = Codec.parseICS(data.trim());
      for (const e of parsed) {
        events.push({
          ...e,
          accountId: this.accountId,
          calendarId,
          etag: etag ? etag.trim() : null,
          _href: _absoluteUrl(calendarId, href.trim()),
        });
      }
    }
    return events;
  }

  // ── Writes ──
  /**
   * @param {string} calendarId
   * @param {string} providerId
   * @returns {string}
   */
  _eventUrl(calendarId: any, providerId: any) {
    const safe = encodeURIComponent(providerId).replace(/%2F/g, '/');
    return `${calendarId.replace(/\/$/, '')}/${safe}.ics`;
  }

  /**
   * @param {string} calendarId
   * @param {any} event
   * @returns {Promise<any>}
   */
  async createEvent(calendarId: any, event: any) {
    const providerId = event.providerId || Codec.makeUid();
    const ics = Codec.buildICS({ ...event, providerId });
    const url = this._eventUrl(calendarId, providerId);
    const resp = await Transport.request({
      url,
      method: 'PUT',
      headers: { 'Content-Type': 'text/calendar; charset=utf-8', 'If-None-Match': '*' },
      body: ics,
      ...this._requestOpts(),
    });
    return { providerId, etag: resp.headers?.get?.('ETag') };
  }

  /**
   * @param {string} calendarId
   * @param {string} providerId
   * @param {any} event
   * @param {any} [opts]
   * @returns {Promise<any>}
   */
  async updateEvent(calendarId: any, providerId: any, event: any, { etag } = {} as any) {
    const ics = Codec.buildICS({ ...event, providerId });
    const url = this._eventUrl(calendarId, providerId);
    const headers = { 'Content-Type': 'text/calendar; charset=utf-8' };
    if (etag) (headers as any)['If-Match'] = etag;
    const resp = await Transport.request({
      url,
      method: 'PUT',
      headers,
      body: ics,
      ...this._requestOpts(),
    });
    return { etag: resp.headers?.get?.('ETag') };
  }

  /**
   * @param {string} calendarId
   * @param {string} providerId
   * @param {any} [opts]
   * @returns {Promise<void>}
   */
  async deleteEvent(calendarId: any, providerId: any, { etag } = {} as any) {
    const url = this._eventUrl(calendarId, providerId);
    const headers = {};
    if (etag) (headers as any)['If-Match'] = etag;
    try {
      await Transport.request({ url, method: 'DELETE', headers, ...this._requestOpts() });
    } catch (e: any) {
      // 404 → already gone, idempotent
      if (e && e.code === ERROR_CODES.NOT_FOUND) return;
      throw e;
    }
  }
}

// ── Helpers ──
/**
 * @param {string} base
 * @param {string} ref
 * @returns {string}
 */
function _absoluteUrl(base: any, ref: any) {
  try {
    return new URL(ref, base).toString();
  } catch {
    return ref;
  }
}

/**
 * @param {string} iso
 * @returns {string} a CalDAV UTC timestamp (YYYYMMDDTHHMMSSZ), or ''.
 */
function _toCaldavDt(iso: any) {
  // Same basic-format UTC stamp the codec emits for timed DTSTART/DTEND; reuse
  // it (|| '' keeps the empty-string-on-missing/invalid contract) rather than
  // re-hand-rolling the toISOString().replace(...) idiom.
  return Codec._formatDt(iso, false) || '';
}

// ── CalDAV / WebDAV multistatus parsing (namespace-agnostic) ───────────────
// CalDAV servers disagree on namespacing: Fastmail / Nextcloud emit prefixed
// elements (`<d:response>`, `<c:calendar/>`), while iCloud emits a DEFAULT
// namespace (`<response xmlns="DAV:">`) with attribute-bearing resourcetype
// children (`<calendar xmlns="urn:..."/>`). Regex matching keyed on a `prefix:`
// silently dropped iCloud's entire response. DOMParser + local-name lookups
// (`getElementsByTagNameNS('*', name)` matches any namespace) read every variant,
// and textContent decodes XML entities for free.

/** @param {any} xml @returns {any} the parsed XML document. */
function _parseXml(xml: any): any {
  if (typeof DOMParser === 'undefined') {
    throw new CalendarProviderError(ERROR_CODES.UNSUPPORTED, 'DOMParser unavailable');
  }
  const doc = new DOMParser().parseFromString(String(xml || ''), 'application/xml');
  // parseFromString never throws; a malformed body yields a <parsererror> node.
  if (doc.getElementsByTagName('parsererror').length) {
    throw new CalendarProviderError(ERROR_CODES.UNSUPPORTED, 'malformed CalDAV XML');
  }
  return doc;
}

/** @param {any} root @returns {any[]} the multistatus <response> elements. */
function _responseEls(root: any): any[] {
  return Array.from(root.getElementsByTagNameNS('*', 'response'));
}

/** First descendant element with the given local name (any namespace), or null. */
function _firstEl(el: any, localName: string): any {
  return el.getElementsByTagNameNS('*', localName)[0] || null;
}

/** Text of the first descendant with the local name, or null (un-trimmed). */
function _firstText(el: any, localName: string): string | null {
  const n = _firstEl(el, localName);
  return n && n.textContent != null ? n.textContent : null;
}

/** A <response>'s resource href — its DIRECT-child <href>, not a nested prop
 *  href (e.g. calendar-home-set's), falling back to the first descendant. */
function _responseHref(response: any): string | null {
  for (const c of Array.from(response.children) as any[]) {
    if (c.localName === 'href') return c.textContent;
  }
  return _firstText(response, 'href');
}

/** True when a <response>'s resourcetype includes a CALDAV <calendar/>. */
function _isCalendarCollection(response: any): boolean {
  const rt = _firstEl(response, 'resourcetype');
  return !!rt && rt.getElementsByTagNameNS('*', 'calendar').length > 0;
}

/** The component `name`s of the calendar's supported-calendar-component-set. */
function _supportedComps(response: any): string[] {
  const set = _firstEl(response, 'supported-calendar-component-set');
  if (!set) return [];
  return Array.from(set.getElementsByTagNameNS('*', 'comp')).map(
    (c: any) => c.getAttribute('name') || ''
  );
}

// Phase 4.7: explicit ESM registration. Importing the registry forces it to
// evaluate first (esbuild resolves the order deterministically), so the
// main.js load order no longer has to place the registry before the providers.
ProviderRegistry.register('caldav', CalDavProvider);
